import { Injectable } from '@angular/core';
import { environment } from '@kempower-nx-workspace/receipt-lookup-environment';

declare global {
    interface Window {
        AwsWafCaptcha: any;
    }
}

@Injectable({ providedIn: 'root' })
export class CaptchaService {
    private rendering = false;
    private currentPromise: Promise<void> | null = null;
    readonly captchaElementId = 'aws-waf-captcha';

    /** Show CAPTCHA if no token yet; otherwise resolve immediately */
    showPromptIfNoCookie(): Promise<void> {
        if (document.cookie.includes('aws-waf-token=')) return Promise.resolve();
        return this.prompt();
    }

    prompt(): Promise<void> {
        // Return the existing promise if CAPTCHA is already rendering
        if (this.rendering && this.currentPromise) {
            return this.currentPromise;
        }

        const el = document.getElementById(this.captchaElementId) || this.addContainer();
        el.style.display = 'block';
        this.rendering = true;

        // Store the promise so subsequent calls can return the same one
        this.currentPromise = new Promise<void>((resolve, reject) => {
            window.AwsWafCaptcha.renderCaptcha(el, {
                apiKey: environment.waf.captchaSiteKey,
                onSuccess: () => {
                    this.rendering = false;
                    this.currentPromise = null;
                    el.style.display = 'none';
                    resolve();
                },
                onError: (e: any) => {
                    this.rendering = false;
                    this.currentPromise = null;
                    el.style.display = 'none';
                    reject(e);
                },
            });
        });

        return this.currentPromise;
    }

    private addContainer(): HTMLElement {
        const existingElement = document.getElementById(this.captchaElementId);
        if (existingElement) {
            return existingElement;
        }

        const div = document.createElement('div');
        div.id = this.captchaElementId;
        document.body.appendChild(div);
        return div;
    }

    private scriptLoaded = false;

    /** Load the CAPTCHA script dynamically for each environment */
    async loadCaptchaScript(): Promise<void> {
        if (this.scriptLoaded) {
            return Promise.resolve();
        }

        const scriptUrl = environment.waf.captchaScriptUrl;
        if (!scriptUrl) {
            console.warn('CAPTCHA script URL not configured');
            return Promise.resolve();
        }

        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = scriptUrl;
            script.defer = true;

            script.onload = () => {
                this.scriptLoaded = true;
                resolve();
            };

            script.onerror = (error) => {
                reject(error);
            };

            document.head.appendChild(script);
        });
    }
}
